import React from "react";

const Services = ({ classicHeader, darkTheme, handleNavClick }) => {
  const tiers = [
    {
      name: "Compass",
      price: "$8,000",
      tagline: "Strategic advisory",
      desc: "For founders who need a senior technical voice to think things through with — putting a first team together, getting to launch, or unblocking the engineers you already have. You don't need to be technical yourself; that's usually the point.",
      features: [
        "Weekly 1:1, plus async input between sessions",
        "Architecture and roadmap direction — reviewed with your team, or set from scratch",
        "A plain-language read on what your team or agency is actually delivering",
        "Build-vs-buy and vendor selection sounding board",
        "Hiring help — who to hire first, what to ask, whether to say yes",
        "Guidance on AI tooling adoption and policy",
        "Board and investor prep support as needed",
      ],
      ceiling:
        "You'll outgrow this the moment you need decisions made rather than talked through, or the team managed rather than advised. That's Core.",
      highlight: false,
    },
    {
      name: "Core",
      price: "$14,000",
      tagline: "Embedded technical ownership",
      badge: "Most common engagement",
      desc: "For companies that need a real technical leader in the seat without full-time presence — someone who makes the call and will work directly in the code when a problem calls for it.",
      featuresIntro: "Everything in Compass, but owned instead of advised:",
      features: [
        "A standing seat in your leadership cadence",
        "Architecture and roadmap owned and tied to business priorities",
        "Vendor relationships, security review, and the compliance program run on your behalf",
        "Hiring run end to end — job descriptions, interview loops, hire/no-hire call",
        "AI tooling selected, rolled out, and governed by policy",
        "Delivery health measured and reported to leadership",
        "Hands-on in the code on critical paths when the problem calls for it",
      ],
      highlight: true,
    },
    {
      name: "Lead",
      price: "$24,000",
      tagline: "Full technical leadership",
      desc: "For companies in a high-stakes window — fundraise prep, a platform migration, an active audit cycle, or building engineering leadership from the ground up.",
      featuresIntro: "Everything in Core, at the executive level:",
      features: [
        "The engineering org owned — 1:1s, performance management, growth plans, hire/fire authority",
        "Org design and engineering leadership build-out",
        "Compliance and AI governance owned at the strategy and board-reporting level",
        "Board-level reporting on the technology function",
        "Personally in the work during critical windows — incidents, audit weeks, fundraise diligence",
      ],
      highlight: false,
    },
  ];

  const commitmentPricing = [
    { tier: "Compass", monthly: "$8,000/mo", sixMonth: "$7,600/mo", twelveMonth: "$7,200/mo" },
    { tier: "Core", monthly: "$14,000/mo", sixMonth: "$13,300/mo", twelveMonth: "$12,600/mo" },
    { tier: "Lead", monthly: "$24,000/mo", sixMonth: "$22,800/mo", twelveMonth: "$21,600/mo" },
  ];

  return (
    <section
      id="services"
      className={"section " + (darkTheme ? "bg-dark-2" : "bg-light")}
    >
      <div className={"container " + (classicHeader ? "" : "px-lg-5")}>
        {/* Heading */}
        <div className="position-relative d-flex text-center mb-5">
          <h2
            className={
              "text-24  text-uppercase fw-600 w-100 mb-0 " +
              (darkTheme ? "text-white-50  opacity-1" : "text-light  opacity-4")
            }
          >
            Services
          </h2>
          <p
            className={
              "text-9  fw-600 position-absolute w-100 align-self-center lh-base mb-0 " +
              (darkTheme ? "text-white" : "text-dark")
            }
          >
            How we can work together
            <span className="heading-separator-line border-bottom border-3 border-primary d-block mx-auto" />
          </p>
        </div>
        {/* Heading end*/}

        {/* Intro copy */}
        <div className="row">
          <div className="col-lg-9 mx-auto text-center mb-5">
            <h3
              className={
                "text-6 fw-600 mb-3 " + (darkTheme ? "text-white" : "text-dark")
              }
            >
              Fractional CTO Engagement Tiers
            </h3>
            <p className={darkTheme ? "text-white-50" : "text-muted"}>
              All three tiers cover the same ground — architecture, hiring,
              vendors and compliance, AI tooling. What changes is how much of
              it I own: advising the founder who makes the call, making the
              call myself, or carrying the whole technology function at the
              executive level. Every tier is a monthly retainer with no
              long-term lock-in required, though multi-month commitments are
              priced accordingly.
            </p>
          </div>
        </div>

        {/* Pricing tiers */}
        <div className="row">
          {tiers.map((tier, index) => (
            <div className="col-lg-4 mb-4 d-flex" key={index}>
              <div
                className={
                  "featured-box style-4 w-100 shadow-sm rounded p-4 d-flex flex-column " +
                  (darkTheme ? "bg-dark-1" : "bg-white") +
                  (tier.highlight ? " border border-primary border-2" : "")
                }
              >
                <div className="d-flex align-items-center justify-content-center mb-3" style={{ height: "24px" }}>
                  {tier.badge && (
                    <span className="badge bg-primary text-2 fw-400 text-nowrap">
                      {tier.badge}
                    </span>
                  )}
                </div>
                <h3 className={`mb-1 ${darkTheme ? "text-white" : "text-dark"}`}>
                  {tier.name}
                </h3>
                <div className="mb-2">
                  <span
                    className={`text-8 fw-700 ${darkTheme ? "text-white" : "text-dark"
                      }`}
                  >
                    {tier.price}
                  </span>
                  <span className={darkTheme ? "text-white-50" : "text-muted"}>
                    /month
                  </span>
                </div>
                <p
                  className={`text-4 fw-500 mb-3 ${darkTheme ? "text-primary" : "text-primary"
                    }`}
                >
                  <em>{tier.tagline}</em>
                </p>
                <p className={`mb-3 ${darkTheme ? "text-white-50" : "text-muted"}`}>
                  {tier.desc}
                </p>

                {tier.featuresIntro && (
                  <p
                    className={`fw-600 mb-2 ${darkTheme ? "text-white" : "text-dark"
                      }`}
                  >
                    {tier.featuresIntro}
                  </p>
                )}

                <ul
                  className={`list-unstyled small mb-4 ${darkTheme ? "text-white-50" : "text-muted"
                    }`}
                >
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="mb-2">
                      <i className="fas fa-check text-primary me-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>

                {tier.ceiling && (
                  <p
                    className={`small mb-4 pt-3 border-top ${darkTheme ? "text-white-50 border-secondary" : "text-muted"
                      }`}
                  >
                    <em>{tier.ceiling}</em>
                  </p>
                )}

                <div className="mt-auto pt-2">
                  <a
                    href="#contact"
                    className="btn btn-primary rounded-pill shadow-none smooth-scroll w-100"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick && handleNavClick("contact");
                    }}
                  >
                    Let's Talk
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Notes */}
        <div className="row mt-4">
          <div className="col-lg-6 mb-4">
            <h4
              className={`text-5 fw-600 mb-3 ${darkTheme ? "text-white" : "text-dark"
                }`}
            >
              A Note on Compliance Experience
            </h4>
            <p className={darkTheme ? "text-white-50" : "text-muted"}>
              Production experience with HIPAA and SOC 2 compliant
              systems informs every engagement above Compass. In practice,
              that means knowing what an auditor will ask before they ask it,
              and which vendor compliance claims hold up and which don't. The
              compliance program — roadmap, vendor relationships, audit
              cadence, and translating risk into board-level language — is
              owned and managed as part of Core and Lead engagements.
              Hands-on execution of individual controls is resourced
              separately, through your internal team or a specialized
              contractor, to keep the right skillset on the right task.
            </p>
          </div>
          <div className="col-lg-6 mb-4">
            <h4
              className={`text-5 fw-600 mb-3 ${darkTheme ? "text-white" : "text-dark"
                }`}
            >
              A Note on AI Tooling &amp; Governance
            </h4>
            <p className={darkTheme ? "text-white-50" : "text-muted"}>
              As AI coding tools and agentic workflows move from experiment
              to default practice, ungoverned adoption creates its own risk:
              inconsistent model access, contractors and employees working
              from different tools with no shared context, and no visibility
              into how AI-assisted code reaches production. Core and Lead
              engagements include governing this layer directly — evaluating
              and configuring the tools your team actually uses, setting
              policy for how AI-assisted work gets reviewed, and keeping
              contractor and vendor access consistent with the rest of your
              security posture.
            </p>
          </div>
        </div>

        {/* Commitment pricing */}
        <div className="row mt-2">
          <div className="col-lg-10 mx-auto">
            <h4
              className={`text-5 fw-600 mb-3 text-center ${darkTheme ? "text-white" : "text-dark"
                }`}
            >
              Commitment Pricing
            </h4>
            <p
              className={`text-center mb-4 ${darkTheme ? "text-white-50" : "text-muted"
                }`}
            >
              Month-to-month is always available at the base rate. Companies
              that commit to a longer engagement lock in a lower effective
              rate.
            </p>
            <div className="table-responsive">
              <table
                className={
                  "table text-center align-middle " +
                  (darkTheme ? "table-dark" : "table-bordered bg-white")
                }
              >
                <thead>
                  <tr>
                    <th>Tier</th>
                    <th>Month-to-Month</th>
                    <th>6-Month Commitment</th>
                    <th>12-Month Commitment</th>
                  </tr>
                </thead>
                <tbody>
                  {commitmentPricing.map((row, index) => (
                    <tr key={index}>
                      <td className="fw-600">{row.tier}</td>
                      <td>{row.monthly}</td>
                      <td>{row.sixMonth}</td>
                      <td>{row.twelveMonth}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p
              className={`text-center mt-4 mb-0 ${darkTheme ? "text-white-50" : "text-muted"
                }`}
            >
              <em>
                Equity components are considered on a case-by-case basis for
                select engagements.
              </em>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
